import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'

import Breadcrumbs from '../../components/breadcrumbs'
import Footer from '../../components/footer'
import DarkNav from '../../components/dark-nav'
import { getCanonicalUrl } from '../../utils/canonical'
import './cliniko.css'
import './ai-assistant-page.css'

export const CheckList = ({ items }) => (
  <div className="cliniko-requirements">
    {items.map((r, i) => (
      <div key={i} className="cliniko-requirement">
        <span className="cliniko-requirement-icon">✓</span>
        <span>{r}</span>
      </div>
    ))}
  </div>
)

export const Steps = ({ items }) => (
  <div className="cliniko-steps">
    {items.map((s, i) => (
      <div key={i} className="cliniko-step">
        <div className="cliniko-step-num">{i + 1}</div>
        <div className="cliniko-step-content">
          {typeof s === 'string' ? <p>{s}</p> : s}
        </div>
      </div>
    ))}
  </div>
)

export const CodeBlock = ({ code }) => {
  const [copied, setCopied] = useState(false)
  const copy = () => {
    navigator.clipboard.writeText(code).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    })
  }
  return (
    <div className="ai-code-block">
      <pre><code>{code}</code></pre>
      <button type="button" className="ai-code-copy" onClick={copy}>{copied ? 'Copied' : 'Copy'}</button>
    </div>
  )
}

export const Section = ({ label, title, intro, dark, id, children }) => (
  <section className={`cliniko-section${dark ? ' cliniko-section--dark' : ''}`} id={id}>
    <div className="cliniko-inner">
      <div className="cliniko-section-header">
        {label && <span className="cliniko-label">{label}</span>}
        <h2>{title}</h2>
        {intro && <p>{intro}</p>}
      </div>
      {children}
    </div>
  </section>
)

// Sections that are identical on every AI assistant page; only the assistant's name changes.
export const AssistantSharedSections = ({ assistant }) => (
  <>
    <Section label="Capabilities" title="What you can do" dark>
      <CheckList items={[
        'Send SMS to up to 10 numbers at once, from your own sender ID or number',
        'Send campaigns to your DataFlows contact groups',
        'Preview before you send: recipients, SMS parts, credits needed, and warnings for emoji, long messages, a missing opt-out line or blocked words',
        'Schedule messages for later, see what is scheduled, and cancel anything before it sends',
        'Check the delivery status of messages you sent and read replies',
        'See campaign results and a usage summary of messages sent, credits used and delivery rate',
        'Check your credit balance and available senders',
      ]} />
    </Section>

    <Section label="Try it" title="Example prompts">
      <div className="ai-prompts">
        {[
          "What's my DataFlows SMS balance and which sender IDs can I use?",
          "Send 'Your order is ready for pickup' to 0404 235 133 from DataFlows.",
          "Preview a message to my Demo customers group saying 'We're closed on Monday for the public holiday', then send it once I confirm.",
          "Schedule 'Reminder: your appointment is tomorrow at 10am' to 0404 235 133 for 9am tomorrow.",
          'Did my messages today get delivered, and did anyone reply?',
          'Summarise my SMS usage for the last 30 days.',
        ].map((p, i) => (
          <div key={i} className="ai-prompt">“{p}”</div>
        ))}
      </div>
    </Section>

    <Section label="Safety" title="Built-in safeguards" dark>
      <CheckList items={[
        `${assistant} asks you to confirm before any message is sent`,
        'A group send must match the recipient count you previewed',
        'The same sender ID registration, opt-out and blocklist rules as the DataFlows dashboard apply',
      ]} />
    </Section>

    <Section label="Privacy" title="Privacy and support">
      <p className="ai-body-text">
        See our <Link to="/privacy-policy#ai-assistant-connections" className="cliniko-inline-link">Privacy Policy</Link> for
        what the connector can access. For help, email <a href="mailto:support@dataflows.com.au" className="cliniko-inline-link">support@dataflows.com.au</a>.
      </p>
    </Section>
  </>
)

const AiAssistantPage = ({ slug, name, title, description, heading, intro, badge, children, disconnect, relatedLink, heroAction }) => {
  const [showScrollTop, setShowScrollTop] = useState(false)

  useEffect(() => {
    const handleScroll = () => setShowScrollTop(window.scrollY > 300)
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  const path = `/integrations/${slug}`

  return (
    <div className="cliniko-container">
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={getCanonicalUrl(path)} />
        <link rel="canonical" href={getCanonicalUrl(path)} />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://dataflows.com.au/" },
            { "@type": "ListItem", "position": 2, "name": "Integrations", "item": "https://dataflows.com.au/integrations" },
            { "@type": "ListItem", "position": 3, "name": name, "item": `https://dataflows.com.au${path}` }
          ]
        })}</script>
      </Helmet>

      <DarkNav />

      <div className="cliniko-breadcrumbs">
        <div className="cliniko-inner">
          <Breadcrumbs items={[{ label: 'Home', path: '/' }, { label: 'Integrations', path: '/integrations' }, { label: name }]} />
        </div>
      </div>

      <section className="cliniko-hero">
        <div className="cliniko-inner">
          <div className="cliniko-hero-badge">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
            {badge}
          </div>
          <h1 className="cliniko-hero-title">{heading}</h1>
          <p className="cliniko-hero-subtitle">{intro}</p>
          <div className="cliniko-hero-actions">
            {heroAction}
            <a href="#connect" className="cliniko-btn-primary">
              How to connect
              <svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clipRule="evenodd"/></svg>
            </a>
            <a href="https://sms.dataflows.com.au/register" target="_blank" rel="noreferrer noopener" className="cliniko-btn-outline">
              Create a DataFlows account
            </a>
          </div>
        </div>
      </section>

      {children}

      <AssistantSharedSections assistant={name} />

      {disconnect && (
        <Section label="Disconnect" title="Disconnect" dark>
          <p className="ai-body-text">{disconnect}</p>
        </Section>
      )}

      <section className="cliniko-section">
        <div className="cliniko-inner">
          <h2 className="cliniko-related-title">Related Integrations</h2>
          <div className="cliniko-related-links">
            {relatedLink}
            <Link to="/integrations/zapier" className="cliniko-related-link">Zapier SMS</Link>
            <Link to="/integrations/power-automate" className="cliniko-related-link">Power Automate SMS</Link>
            <Link to="/integrations" className="cliniko-related-link">All integrations</Link>
          </div>
        </div>
      </section>

      <Footer />

      {showScrollTop && (
        <div className="cliniko-scroll-top show" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
          <svg viewBox="0 0 24 24" fill="none"><path d="M12 5L19 12H16V19H8V12H5L12 5Z" fill="currentColor"/></svg>
        </div>
      )}
    </div>
  )
}

export default AiAssistantPage
